import { datadogRum } from '@datadog/browser-rum'
import ReactGA from 'react-ga4'
import Hotjar from '@hotjar/browser'
import useAcceptCheckoutRule from '../graphQL/mutations/acceptCheckoutRule'
import { SessionDataArgs } from '../graphQL/queries/getSession/types'
import useAppDispatch from './useAppDispatch'
import { ICart, LoadersType } from '../interfaces'
import useModal from './useModal'
import { validateIsAlcohol, validateIsReturnable } from '../utils/validations'
import { addLoader, removeLoader } from '../redux/slices/config'
import postCreateOrder from '../services/postCreateOrder'
import { generateDataDogContext } from '../utils/dataDog'
import { ICheckoutRulesConstraints } from '../graphQL/commons'
import { sendGoogleEvent } from '../utils/sendGoogleAnalytics'
import useAppSelector from './useAppSelector'
import getAnalyticsItems from '../utils/getAnalyticsItems'
import { getAnalyticsPurchasePayload } from '../utils/getAnalyticsPayload'
import { fireGTMEvent } from '../utils/fireGTMEvent'
import useOrderConfirmation from './orders/useOrderConfirmation'
import { isOrderSplitStorefront } from '../utils/orderConfirmation'

export const useCreateOrderHook = () => {
  const { closeModal } = useModal()
  const dispatch = useAppDispatch()
  const [acceptCheckoutRule] = useAcceptCheckoutRule()
  const { createAndConfirmOrder } = useOrderConfirmation()
  const state = useAppSelector((stateRedux) => stateRedux)
  const { config } = state.defaultSlice

  const CreateOrder = async ({
    sessionId,
    storeFrontName,
    phone,
    cart,
    session,
  }: {
    sessionId: string
    storeFrontName: string
    phone: string
    cart: ICart
    session: SessionDataArgs
  }) => {
    const { configuration } = session

    const isAlcoholActive =
      configuration?.checkoutRules?.cartConditionPendingUserValidation.isActive
    const isReturnableActive =
      configuration?.checkoutRules?.cartWarningCheckReturnables.isActive
    // --> checkout rules
    if (validateIsReturnable(cart) && isReturnableActive) {
      acceptCheckoutRule({
        variables: {
          storefrontName: storeFrontName,
          sessionUid: sessionId,
          ruleType: ICheckoutRulesConstraints.CART_WARNING_CHECK_RETURNABLES,
        },
      })
    }
    if (validateIsAlcohol(cart) && isAlcoholActive) {
      acceptCheckoutRule({
        variables: {
          storefrontName: storeFrontName,
          sessionUid: sessionId,
          ruleType:
            ICheckoutRulesConstraints.CART_CONDITION_PENDING_USER_VALIDATION,
        },
      })
    }
    // <-- checkout rules
    closeModal({ container: 'confirmOrder' })
    dispatch(addLoader({ name: LoadersType.CREATE_ORDER, delay: 0 }))
    const category = 'cart'
    const action = 'Create Order'
    Hotjar.event(action)
    sendGoogleEvent(category, action)
    window.fbq('trackCustom', 'CreateOrder')
    const cartAnalyticsItems = getAnalyticsItems(cart)
    const analyticsPayload = getAnalyticsPurchasePayload(
      cart,
      cartAnalyticsItems,
      session,
      config,
    )
    ReactGA.event('purchase', analyticsPayload)
    window.fbq('track', 'Purchase', analyticsPayload)
    fireGTMEvent('commerce-purchase', {
      currency: analyticsPayload.currency,
      value: analyticsPayload.value,
      items: analyticsPayload.items,
      transaction_id: analyticsPayload.transaction_id,
    })
    /*
     * The opt-in branch, and the only behavioural fork in this hook.
     *
     * Compared against literal `true` because the storefront config is remote
     * JSON with no boolean guarantee — a quoted `"false"` is truthy, and would
     * silently move a storefront onto a path whose prerequisite is a Builder
     * flow edit it has not had.
     *
     * Everything above this point is shared, including the `purchase` events.
     * They fire before the order exists, which is correct for the legacy path
     * — the flow creates it moments later, in chat — and is left alone here
     * rather than moved after `createOrder`, because moving them would change
     * the estate's analytics to fix nothing this ticket claims.
     */
    const optedIn = config?.options?.inWebviewOrderConfirmation === true
    const splitForbidsOptIn = optedIn && isOrderSplitStorefront(session)

    /*
     * An opt-in the server will refuse on every attempt, so it is declined
     * here instead.
     *
     * `createOrder` and `confirmOrder` both throw `BAD_REQUEST` outright for a
     * splitting storefront, naming `createOrders`/`confirmOrders` — which
     * cinnamon does not implement. Taking the branch anyway would break
     * checkout completely for that storefront and surface as a generic
     * creation failure naming no checkout rules, with the actual cause
     * (`inWebviewOrderConfirmation` authored beside an active order splitter)
     * visible nowhere.
     *
     * Falls through to the legacy path, which is what these storefronts use
     * today and what works. Reported rather than thrown for the reason the
     * `orderConfirmation` guards give: the shopper's checkout is worth more
     * than the authoring feedback, and this is the only place the combination
     * surfaces at all.
     */
    if (splitForbidsOptIn) {
      const context = generateDataDogContext({
        title: 'inWebviewOrderConfirmation unusable on a splitting storefront',
        extraInfo: {
          function: 'useCreateOrderHook',
          maxProduct: session?.configuration?.splitters?.order?.maxProduct,
        },
      })
      datadogRum.startView(context.viewName)
      datadogRum.addError(
        new Error(
          'inWebviewOrderConfirmation is authored but the storefront splits orders; createOrder/confirmOrder would reject every attempt, so the legacy path was used',
        ),
        context,
      )
    }

    if (optedIn && !splitForbidsOptIn) {
      try {
        await createAndConfirmOrder({ session, phone })
      } finally {
        /*
         * The loader has to come down here, and only here.
         *
         * `addLoader` above is matched by `removeLoader` in the legacy catch
         * alone, which works because the legacy success path navigates away
         * and takes the whole document with it. Suppress that redirect and the
         * same code leaves a full-screen loader over the confirmation screen
         * forever.
         *
         * `finally` rather than a line after the await, and note what that
         * does and does not buy. `createAndConfirmOrder` does not currently
         * reject — every await inside it sits in a try/catch — so today a
         * plain line after the await would behave identically and no test
         * distinguishes them. What `finally` defends is the absence of that
         * property from any contract: it is an internal detail of a 450-line
         * hook, and the failure if it changes is a shopper stuck under a
         * full-screen loader with no way out.
         *
         * Deliberately not hoisted to cover both branches: removing it on the
         * legacy path would flash the cart back at a shopper mid-navigation,
         * which is a visible change to every storefront in the estate.
         *
         * `createAndConfirmOrder` returns once the outcome is terminal or
         * being polled for — the poll continues on a timer — so this does not
         * hold the loader up for the length of a callback wait.
         */
        dispatch(removeLoader(LoadersType.CREATE_ORDER))
      }
      return
    }

    try {
      const workflowApi = process.env.REACT_APP_WORKFLOW_API || ''
      const botrunnerApi = process.env.REACT_APP_BOTRUNNER_API || ''
      await postCreateOrder({
        data: session,
        phoneNumber: phone,
        workflowApi,
        botrunnerApi,
      })
    } catch (error) {
      const context = generateDataDogContext({
        title: 'could not create order',
        extraInfo: { phone, action: 'postCreateOrder' },
      })
      datadogRum.startView(context.viewName)
      datadogRum.addError(error, context)
      dispatch(removeLoader(LoadersType.CREATE_ORDER))
    }
  }
  return CreateOrder
}

export default useCreateOrderHook
